import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type {
  ApiResponse,
  JobChecklistItem,
  JobChecklistPayload,
  JobChecklistTemplate,
  PaginatedResponse,
} from '@/types'

const unwrapPayload = <T>(payload: ApiResponse<T> | T | null | undefined): T | null => {
  if (!payload) {
    return null
  }
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as ApiResponse<T>).data ?? null
  }
  return payload as T
}

export const checklistApi = {
  getJobChecklist: async (jobId: string, workDayId?: string | number, kind: 'job' | 'project' | 'event' = 'job'): Promise<JobChecklistPayload> => {
    const params = workDayId ? { work_day_id: String(workDayId) } : undefined
    const checklistPath =
      kind === 'project'
        ? API_PATHS.jobs.checklistProject(jobId)
        : kind === 'event'
          ? API_PATHS.jobs.checklistEvent(jobId)
          : API_PATHS.jobs.checklist(jobId)
    const { data } = await api.get<ApiResponse<JobChecklistPayload> | JobChecklistPayload>(
      checklistPath,
      { params, skipPostIdHeader: true } as any,
    )
    const payload = unwrapPayload(data)
    if (!payload) {
      return { items: [], summary: { total: 0, completed: 0, required_total: 0, required_completed: 0, all_required_complete: true } }
    }
    return payload
  },

  addJobChecklistItem: async (
    jobId: string,
    item: {
      title: string
      description?: string
      is_required?: boolean
      requires_photo?: boolean
      requires_signature?: boolean
      work_day_id?: string | number
    },
    kind: 'job' | 'project' | 'event' = 'job',
  ): Promise<JobChecklistItem> => {
    const addPath =
      kind === 'project'
        ? API_PATHS.jobs.checklistAddProject(jobId)
        : kind === 'event'
          ? API_PATHS.jobs.checklistAddEvent(jobId)
          : API_PATHS.jobs.checklistAdd(jobId)
    const { data } = await api.post<ApiResponse<JobChecklistItem> | JobChecklistItem>(
      addPath,
      item,
    )
    const payload = unwrapPayload(data)
    if (!payload) {
      throw new Error('Missing checklist item response.')
    }
    return payload
  },

  applyTemplate: async (jobId: string, kind: 'job' | 'project' | 'event' = 'job'): Promise<JobChecklistPayload> => {
    const applyPath =
      kind === 'project'
        ? API_PATHS.jobs.checklistApplyTemplateProject(jobId)
        : kind === 'event'
          ? API_PATHS.jobs.checklistApplyTemplateEvent(jobId)
          : API_PATHS.jobs.checklistApplyTemplate(jobId)
    const { data } = await api.post<ApiResponse<JobChecklistPayload> | JobChecklistPayload>(
      applyPath,
      {},
    )
    const payload = unwrapPayload(data)
    if (!payload) {
      throw new Error('Missing checklist response.')
    }
    return payload
  },

  completeItem: async (itemId: string | number, notes?: string): Promise<JobChecklistItem> => {
    const { data } = await api.post<ApiResponse<JobChecklistItem> | JobChecklistItem>(
      API_PATHS.jobChecklist.itemComplete(itemId),
      notes ? { notes } : {},
    )
    const payload = unwrapPayload(data)
    if (!payload) {
      throw new Error('Missing checklist item response.')
    }
    return payload
  },

  reopenItem: async (itemId: string | number): Promise<JobChecklistItem> => {
    const { data } = await api.post<ApiResponse<JobChecklistItem> | JobChecklistItem>(
      API_PATHS.jobChecklist.itemReopen(itemId),
      {},
    )
    const payload = unwrapPayload(data)
    if (!payload) {
      throw new Error('Missing checklist item response.')
    }
    return payload
  },

  deleteItem: async (itemId: string | number): Promise<void> => {
    await api.delete(API_PATHS.jobChecklist.itemDelete(itemId))
  },

  getTemplates: async (params?: { kind?: 'job' | 'project' | 'event' }): Promise<PaginatedResponse<JobChecklistTemplate>> => {
    const listPath =
      params?.kind === 'project'
        ? API_PATHS.jobChecklist.templateListProject
        : params?.kind === 'event'
          ? API_PATHS.jobChecklist.templateListEvent
          : API_PATHS.jobChecklist.templateList
    const { data } = await api.get<ApiResponse<JobChecklistTemplate[] | PaginatedResponse<JobChecklistTemplate>>>(
      listPath,
      { params: params?.kind ? { kind: params.kind } : undefined, skipPostIdHeader: true } as any,
    )
    const payload = unwrapPayload(data)
    if (Array.isArray(payload)) {
      return {
        count: payload.length,
        next: null,
        previous: null,
        results: payload,
      }
    }
    if (payload && typeof payload === 'object' && 'results' in payload) {
      return payload as PaginatedResponse<JobChecklistTemplate>
    }
    return { count: 0, next: null, previous: null, results: [] }
  },

  getTemplate: async (id: string | number): Promise<JobChecklistTemplate> => {
    const { data } = await api.get<ApiResponse<JobChecklistTemplate> | JobChecklistTemplate>(
      API_PATHS.jobChecklist.templateGet(id),
      { skipPostIdHeader: true } as any,
    )
    const payload = unwrapPayload(data)
    if (!payload) {
      throw new Error('Missing checklist template response.')
    }
    return payload
  },

  createTemplate: async (payload: {
    name: string
    description?: string
    kind?: 'job' | 'project' | 'event'
    job_category?: string | number | null
    is_default?: boolean
    active?: boolean
    items?: Array<{
      title: string
      description?: string
      scope?: 'job' | 'work_day'
      is_required?: boolean
      requires_photo?: boolean
      requires_signature?: boolean
      sort_order?: number
    }>
  }): Promise<JobChecklistTemplate> => {
    const { data } = await api.post<ApiResponse<JobChecklistTemplate> | JobChecklistTemplate>(
      API_PATHS.jobChecklist.templateAdd,
      payload,
    )
    const result = unwrapPayload(data)
    if (!result) {
      throw new Error('Missing checklist template response.')
    }
    return result
  },

  updateTemplate: async (
    id: string | number,
    payload: {
      name: string
      description?: string
      kind?: 'job' | 'project' | 'event'
      job_category?: string | number | null
      is_default?: boolean
      active?: boolean
      items?: Array<{
        title: string
        description?: string
        scope?: 'job' | 'work_day'
        is_required?: boolean
        requires_photo?: boolean
        requires_signature?: boolean
        sort_order?: number
      }>
    },
  ): Promise<JobChecklistTemplate> => {
    const { data } = await api.post<ApiResponse<JobChecklistTemplate> | JobChecklistTemplate>(
      API_PATHS.jobChecklist.templateUpdate(id),
      payload,
    )
    const result = unwrapPayload(data)
    if (!result) {
      throw new Error('Missing checklist template response.')
    }
    return result
  },

  deleteTemplate: async (id: string | number): Promise<void> => {
    await api.delete(API_PATHS.jobChecklist.templateDelete(id))
  },
}
